import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { localizePath, stripLocale } from '../utils/localePath';
import './Nav.css';

const Nav = () => {
  const { t, language, toPath } = useLanguage();
  const location = useLocation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const rafRef = useRef(null);

  const updateNavVisibility = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    if (currentScrollY < 100) {
      setIsVisible(true);
    } else if (currentScrollY > lastScrollY.current) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    
    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateNavVisibility);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateNavVisibility]);

  const nextLanguage = language === 'de' ? 'en' : 'de';
  const languageSwitchTo = localizePath(stripLocale(location.pathname), nextLanguage) + location.search + location.hash;

  return (
    <>
    <div className={`nav-fox__floating-icon ${!isVisible && lastScrollY.current > 100 ? 'nav-fox__floating-icon--visible' : ''}`}>
      <Link to={toPath('/')}>
        <img 
          src="/img/fox_icon.png" 
          alt="Fox Performance"
          width={40}
          height={40}
          loading="eager"
        />
      </Link>
    </div>
    
    <nav className={`nav-fox ${!isVisible ? 'nav-fox--hidden' : ''}`}>
      <div className="nav-fox__container">
        <Link to={toPath('/')} className="nav-fox__logo-link">
          <img 
            src="/img/fox_performance_logo.png" 
            alt="Fox Performance" 
            className="nav-fox__logo"
            width={180}
            height={40}
            loading="eager"
            fetchpriority="high"
          />
        </Link>
        <div className="nav-fox__right">
          <div className="nav-fox__links">
            <div 
              className="nav-fox__dropdown"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button className="nav-fox__link nav-fox__link--dropdown">
                {t.nav.services}
                <svg 
                  className={`nav-fox__dropdown-icon ${servicesOpen ? 'nav-fox__dropdown-icon--open' : ''}`}
                  width="10" 
                  height="6" 
                  viewBox="0 0 10 6" 
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {servicesOpen && (
                <div className="nav-fox__dropdown-menu">
                  <a href="#ppc" className="nav-fox__dropdown-item">{t.nav.amazonPPC}</a>
                  <a href="#produktbilder" className="nav-fox__dropdown-item">{t.nav.produktbilder}</a>
                  <a href="#seo" className="nav-fox__dropdown-item">{t.nav.amazonSEO}</a>
                  <Link to={toPath('/portfolio')} className="nav-fox__dropdown-item">{t.nav.designPortfolio}</Link>
                </div>
              )}
            </div>
            <a href="#testimonials" className="nav-fox__link">{t.nav.customerSuccess}</a>
          </div>
          <Link
            to={languageSwitchTo}
            className="nav-fox__lang-toggle"
            hrefLang={nextLanguage}
            aria-label={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
          >
            {language === 'de' ? 'EN' : 'DE'}
          </Link>
          <a
            href="https://meetings-eu1.hubspot.com/michael-wenger?uuid=9cf1d252-81d9-4d0a-ac62-21972be61c70"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-fox__cta-button"
          >
            {t.nav.contact}
          </a>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Nav;
